import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Textarea } from '@codes/ui-kit/components/ui/textarea';

import { AgentFormData } from '@codes/agents';

interface FormFieldProps {
    label: string;
    children: React.ReactNode;
    required?: boolean;
}

const FormField = React.memo<FormFieldProps>(({ label, children, required = false }) => {
    const { t } = useTranslation();

    return (
        <div>
            <label className="inline-block text-base font-medium text-text mb-[9px]">
                {label}
                {required && <span className="text-error ml-1">{t('agent.form.basic.states.required')}</span>}
            </label>
            {children}
        </div>
    );
});

interface AgentFormPersonaProps {
    formData: AgentFormData;
    onChange: (updates: Partial<AgentFormData>) => void;
    isLoading?: boolean;
}

export const AgentFormPersona = React.memo<AgentFormPersonaProps>(({ formData, onChange, isLoading = false }) => {
    const { t } = useTranslation();

    const handleAgeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ age: e.target.value });
        },
        [onChange]
    );

    const handleRoleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ role: e.target.value });
        },
        [onChange]
    );

    const handleGoalChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ goal: e.target.value });
        },
        [onChange]
    );

    const handleMindChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ mind: e.target.value });
        },
        [onChange]
    );

    const handleLikeChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ like: e.target.value });
        },
        [onChange]
    );

    const handleToneChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ tone: e.target.value });
        },
        [onChange]
    );

    const handleGreetChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ greet: e.target.value });
        },
        [onChange]
    );

    const handleIntroChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ intro: e.target.value });
        },
        [onChange]
    );

    const handleBackChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ back: e.target.value });
        },
        [onChange]
    );

    const handleAbuseChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ abuse: e.target.value });
        },
        [onChange]
    );

    const handleThankChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ thank: e.target.value });
        },
        [onChange]
    );

    return (
        <div className="flex-1 grid grid-cols-2 max-md:grid-cols-1 gap-x-4 gap-y-5 h-full overflow-auto p-4">
            <FormField label={t('agent.form.basic.fields.age')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.age')}
                    className="h-[106px]"
                    value={formData.age || ''}
                    onChange={handleAgeChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.role')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.role')}
                    className="h-[106px]"
                    value={formData.role || ''}
                    onChange={handleRoleChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.goal')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.goal')}
                    className="h-[106px]"
                    value={formData.goal || ''}
                    onChange={handleGoalChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.personality')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.personality')}
                    className="h-[106px]"
                    value={formData.mind || ''}
                    onChange={handleMindChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.hobbies')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.hobbies')}
                    className="h-[106px]"
                    value={formData.like || ''}
                    onChange={handleLikeChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.tone')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.tone')}
                    className="h-[106px]"
                    value={formData.tone || ''}
                    onChange={handleToneChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.greeting')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.greeting')}
                    className="h-[106px]"
                    value={formData.greet || ''}
                    onChange={handleGreetChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.introduction')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.introduction')}
                    className="h-[106px]"
                    value={formData.intro || ''}
                    onChange={handleIntroChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.fallback')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.fallback')}
                    className="h-[106px]"
                    value={formData.back || ''}
                    onChange={handleBackChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.abuse')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.abuse')}
                    className="h-[106px]"
                    value={formData.abuse || ''}
                    onChange={handleAbuseChange}
                    disabled={isLoading}
                />
            </FormField>

            <FormField label={t('agent.form.basic.fields.thanks')}>
                <Textarea
                    placeholder={t('agent.form.basic.placeholders.thanks')}
                    className="h-[106px]"
                    value={formData.thank || ''}
                    onChange={handleThankChange}
                    disabled={isLoading}
                />
            </FormField>
        </div>
    );
});
